import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import {
  ShieldCheck, ShieldX, FileText, ChevronDown, ChevronUp, MapPin,
  Clock, Star, Languages, Phone, Mail, X
} from 'lucide-react';
import { format } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

export default function AdminHelpers() {
  const [helpers, setHelpers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchHelpers = async () => {
    try {
      const data = await adminAPI.getPendingHelpers();
      setHelpers(data || []);
    } catch (err) {
      console.error('Failed to load pending helpers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHelpers(); }, []);

  const handleVerify = async (helperId, action) => {
    setActionLoading(helperId);
    try {
      await adminAPI.verifyHelper(helperId, action, action === 'reject' ? rejectNote : undefined);
      setHelpers(prev => prev.filter(h => h.id !== helperId));
      setRejectModal(null);
      setRejectNote('');
    } catch (err) {
      console.error('Failed to verify helper:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-semibold text-navy-900">Helper Verification</h1>
        <p className="text-navy-500 mt-1">
          {helpers.length > 0 ? `${helpers.length} helper${helpers.length > 1 ? 's' : ''} pending verification.` : 'No pending verifications.'}
        </p>
      </div>

      {helpers.length === 0 ? (
        <div className="card p-12 text-center">
          <ShieldCheck className="h-12 w-12 text-green-400 mx-auto mb-3" />
          <p className="text-navy-500">All caught up! No helpers waiting for verification.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {helpers.map(helper => {
            const isExpanded = expandedId === helper.id;
            return (
              <div key={helper.id} className="card">
                {/* Header */}
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 bg-navy-200 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                    {helper.profile_photo_url ? (
                      <img src={helper.profile_photo_url.startsWith('http') ? helper.profile_photo_url : `${UPLOADS_BASE_URL}${helper.profile_photo_url}`} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-navy-600 text-lg font-medium">{helper.first_name?.[0]}{helper.last_name?.[0]}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-semibold text-navy-900">{helper.first_name} {helper.last_name}</h3>
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">Pending</span>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-1 text-xs text-navy-500">
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {helper.email}</span>
                      {helper.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {helper.phone}</span>}
                      {helper.experience_years > 0 && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {helper.experience_years} yrs exp</span>}
                      <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {helper.document_count} doc{helper.document_count !== 1 ? 's' : ''}</span>
                    </div>
                    {helper.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {helper.skills.map(s => (
                          <span key={s} className="px-2 py-0.5 bg-navy-100 text-navy-600 text-xs rounded-full">{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleVerify(helper.id, 'approve')}
                      disabled={actionLoading === helper.id}
                      className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-apple hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      {actionLoading === helper.id ? '...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => setRejectModal(helper.id)}
                      className="px-3 py-1.5 border border-red-200 text-red-600 text-sm font-medium rounded-apple hover:bg-red-50 transition-colors flex items-center gap-1"
                    >
                      <ShieldX className="h-3.5 w-3.5" /> Reject
                    </button>
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : helper.id)}
                      className="p-1.5 hover:bg-navy-100 rounded-apple transition-colors"
                    >
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-navy-100 space-y-4">
                    {helper.bio && (
                      <div>
                        <p className="text-xs font-medium text-navy-500 mb-1">Bio</p>
                        <p className="text-sm text-navy-700">{helper.bio}</p>
                      </div>
                    )}
                    {helper.languages?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-navy-500 mb-1">Languages</p>
                        <div className="flex gap-1.5">
                          {helper.languages.map(l => (
                            <span key={l} className="px-2 py-0.5 bg-primary-50 text-primary-700 text-xs rounded-full">{l}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {helper.documents?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-navy-500 mb-2">Documents</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {helper.documents.map(d => (
                            <a
                              key={d.id}
                              href={`${UPLOADS_BASE_URL}${d.file_path}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 bg-navy-50 rounded-apple p-2.5 hover:bg-navy-100 transition-colors"
                            >
                              <FileText className="h-4 w-4 text-primary-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-navy-900 truncate">{d.file_name}</p>
                                <p className="text-xs text-navy-500">{d.document_type}</p>
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-navy-400">
                      Applied: {helper.user_created_at ? format(new Date(helper.user_created_at), 'PPp') : '-'}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-apple-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-display font-semibold text-navy-900">Reject Helper</h3>
              <button onClick={() => { setRejectModal(null); setRejectNote(''); }} className="p-1 hover:bg-navy-100 rounded-apple">
                <X className="h-5 w-5 text-navy-500" />
              </button>
            </div>
            <p className="text-sm text-navy-600 mb-4">
              Provide a reason for rejection. The helper will be notified.
            </p>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="Reason for rejection..."
              className="input-field w-full mb-4"
              rows={3}
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setRejectModal(null); setRejectNote(''); }} className="btn-secondary">Cancel</button>
              <button
                onClick={() => handleVerify(rejectModal, 'reject')}
                disabled={actionLoading === rejectModal}
                className="px-4 py-2 bg-red-600 text-white rounded-pill text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading === rejectModal ? 'Rejecting...' : 'Reject Helper'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
